import {
  changeSessionBoundary, isSessionBlocked, sessionGeneration, subscribeSession
} from '@cdf/api-client-react';
import { useSyncExternalStore } from 'react';
import { isAuthConfigured } from './auth-config';
import type { AccountInfo } from '@azure/msal-browser';
import { logoutTarget, readLogoutTarget, type SignInHandoff } from './auth-navigation';

// Panel-local intent and credential-free targeting only. MSAL owns all tokens.
const KEY = 'commons.local-sign-out.v1';
export const SIGN_IN_COMPLETION_PREFIX = 'commons.sign-in-complete.v1.';
const SIGN_IN_TTL = 10 * 60_000;
let attempt: string | null = null;
let expiresAt = 0;
let handoff: 'panel' | 'tab' | null = null;
let reloaded = false;
let reloadRequested = false;
let target: AccountInfo | undefined;
function readIntent(): boolean {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === 'object') {
      if ('attempt' in value && typeof value.attempt === 'string') attempt = value.attempt;
      if ('expiresAt' in value && typeof value.expiresAt === 'number') expiresAt = value.expiresAt;
      if ('handoff' in value && (value.handoff === 'panel' || value.handoff === 'tab')) handoff = value.handoff;
      if ('reloaded' in value && value.reloaded === true) reloaded = true;
      if ('target' in value) target = readLogoutTarget(JSON.stringify(value.target));
    }
    return true;
  } catch { return true; }
}
function persist(blocked: boolean): void {
  try {
    if (blocked) sessionStorage.setItem(KEY, JSON.stringify({ attempt, expiresAt, handoff, reloaded, target }));
    else sessionStorage.removeItem(KEY);
  } catch { /* The synchronous in-memory fence still applies when storage is denied. */ }
}
function removeCompletion(id: string): void {
  try { localStorage.removeItem(SIGN_IN_COMPLETION_PREFIX + id); } catch { /* Storage can be denied. */ }
}
function pruneCompletions(): void {
  try {
    const keys = Object.keys(localStorage).filter(key => key.startsWith(SIGN_IN_COMPLETION_PREFIX));
    for (const key of keys) {
      try {
        const value: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
        if (value && typeof value === 'object' && 'expiresAt' in value &&
          typeof value.expiresAt === 'number' && value.expiresAt > Date.now() &&
          value.expiresAt <= Date.now() + SIGN_IN_TTL) continue;
      } catch { /* Discard malformed completion records belonging to this feature. */ }
      localStorage.removeItem(key);
    }
  } catch { /* Storage can be denied. */ }
}
if (typeof sessionStorage !== 'undefined' && isAuthConfigured() && readIntent()) changeSessionBoundary(true);

export function locallySignedOut(): boolean { return isSessionBlocked(); }
export function retainedLogoutAccount(): AccountInfo | undefined { return target; }
export function retireLocalSession(account?: AccountInfo): void {
  if (attempt && handoff === 'panel') removeCompletion(attempt);
  if (account) target = logoutTarget(account);
  attempt = null;
  expiresAt = 0;
  handoff = null;
  reloaded = false;
  reloadRequested = false;
  persist(true);
  changeSessionBoundary(true);
}
export function beginExplicitSignIn(source?: SignInHandoff): string {
  retireLocalSession();
  pruneCompletions();
  attempt = source?.id ?? crypto.randomUUID();
  expiresAt = source?.expiresAt ?? Date.now() + SIGN_IN_TTL;
  handoff = source ? 'tab' : null;
  persist(true);
  return attempt;
}
/** Bind the clicked panel to the same state that the new tab gives to MSAL. */
export function panelSignInHandoff(expected: string): SignInHandoff {
  if (attempt !== expected) throw new Error('Sign-in attempt is no longer current.');
  handoff = 'panel';
  persist(true);
  return { id: expected, expiresAt };
}
export function failExplicitSignIn(expected: string): void {
  if (attempt !== expected) return;
  if (handoff === 'panel') removeCompletion(expected);
  attempt = null;
  expiresAt = 0;
  handoff = null;
  reloaded = false;
  persist(true);
}
/** Called only with an MSAL-verified interactive redirect result. */
export function acceptExplicitSignIn(state: unknown, fromCache: unknown, account?: AccountInfo): boolean {
  if (!attempt || state !== attempt || fromCache !== false || expiresAt <= Date.now() ||
    expiresAt > Date.now() + SIGN_IN_TTL) return false;
  if (handoff === 'tab' && account) {
    try {
      // No credentials or profile data cross contexts. Only this verified result
      // can publish completion; a resolved navigation promise is insufficient.
      localStorage.setItem(SIGN_IN_COMPLETION_PREFIX + attempt,
        JSON.stringify({ id: attempt, expiresAt, account: logoutTarget(account) }));
    } catch { /* This tab remains usable if the browser isolates its storage. */ }
  }
  attempt = null;
  expiresAt = 0;
  handoff = null;
  reloaded = false;
  target = undefined;
  persist(false);
  changeSessionBoundary(false);
  return true;
}
/**
 * Restore only the requesting panel after its verified tab completion.
 * Reload once while still fenced so MSAL initializes its own shared cache.
 * Focus/storage events alone never grant a session, nor select another account.
 */
export function restorePanelSignIn(accounts: AccountInfo[], reload: () => void): AccountInfo | null {
  if (reloadRequested || !locallySignedOut() || handoff !== 'panel' || !attempt || expiresAt <= Date.now()) return null;
  const id = attempt;
  try {
    const raw = localStorage.getItem(SIGN_IN_COMPLETION_PREFIX + id);
    if (!raw || raw.length > 4096) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || !('id' in value) || value.id !== id ||
      !('expiresAt' in value) || value.expiresAt !== expiresAt || !('account' in value)) return null;
    const completed = readLogoutTarget(JSON.stringify(value.account));
    if (!completed) return null;
    if (!reloaded) {
      reloadRequested = true;
      reloaded = true;
      persist(true);
      // If persistence is unavailable, do not create a reload loop.
      const stored = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as { reloaded?: unknown } | null;
      if (stored?.reloaded === true) reload();
      return null;
    }
    const account = accounts.find(candidate => candidate.homeAccountId === completed.homeAccountId &&
      candidate.localAccountId === completed.localAccountId && candidate.tenantId === completed.tenantId &&
      candidate.environment === completed.environment);
    if (!account || !acceptExplicitSignIn(id, false)) return null;
    removeCompletion(id);
    return account;
  } catch { return null; }
}
export function useLocalSession() {
  const generation = useSyncExternalStore(subscribeSession, sessionGeneration, sessionGeneration);
  return { generation, signedOut: locallySignedOut() };
}
